import { Switch, Typography } from '@/ui';

import type { AISettings } from '@/store/api/mcpMemberApi';
import type { FC, ReactNode } from 'react';

interface LayerSwitchesProps {
  coachFirstName: string;
  disabled?: boolean;
  onChange: (next: AISettings) => void;
  settings: AISettings;
}

const Layer: FC<{
  children: ReactNode;
  description: string;
  title: string;
}> = ({ children, description, title }) => (
  <div className="flex items-center gap-4 border-0 border-t border-solid border-cement-200 py-3.5 first-of-type:border-t-0">
    <div className="min-w-0 flex-1">
      <Typography className="font-semibold" variant="body1">
        {title}
      </Typography>
      <Typography className="text-cement-400" variant="body2">
        {description}
      </Typography>
    </div>
    {children}
  </div>
);

const LayerSwitches: FC<LayerSwitchesProps> = ({
  coachFirstName,
  disabled = false,
  onChange,
  settings,
}) => (
  <div>
    <Layer
      description={`What you realized, your strengths, ${coachFirstName || 'your coach'}’s guidance, your action items, and the people involved.`}
      title="Coaching notes"
    >
      <Switch
        inputProps={{
          'aria-label': 'Coaching notes, always on when connected',
          role: 'switch',
        }}
        checked
        disabled
      />
    </Layer>
    <Layer
      description="The story of each session, so your AI can look closer when a note isn’t enough."
      title="Reflections"
    >
      <Switch
        checked={settings.reflections_enabled}
        disabled={disabled}
        inputProps={{ 'aria-label': 'Share reflections', role: 'switch' }}
        onChange={(_event, checked) =>
          onChange({ ...settings, reflections_enabled: checked })
        }
      />
    </Layer>
    <Layer
      description="Your AI can search your sessions for a short excerpt, like something you said. It never gets a full transcript."
      title="Transcript search"
    >
      <Switch
        checked={settings.transcripts_enabled}
        disabled={disabled}
        inputProps={{ 'aria-label': 'Share transcript search', role: 'switch' }}
        onChange={(_event, checked) =>
          onChange({ ...settings, transcripts_enabled: checked })
        }
      />
    </Layer>
  </div>
);

export default LayerSwitches;
